import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { routes } from '@app/router/routes'
import { useAgreementSigning } from '@modules/listingRequest'
import { usePropertyWindowExtensionQuery } from '../api/usePropertyWindowExtensionQuery'
import { extensionNoticeOf, isExtensionActionNeeded } from '../utils/windowExtensionViewUtils'

// The funding window extension on one property: the open proposal, the line the
// banner and the funding window carry, and the seller's two answers.
//
// Only a running window can be extended, so the proposal is read while live.
// Accepting hands off to UAE PASS and lands back on `returnPath` — this property
// unless the caller says otherwise (the Marketplace page does); declining
// records the reason and refreshes the proposal. Both go through the sell
// request's own signing actions — an extension is an agreement on the request.
export function usePropertyWindowExtension(
  propertyId: MaybeRefOrGetter<string>,
  isLive: MaybeRefOrGetter<boolean>,
  options: { returnPath?: MaybeRefOrGetter<string> } = {},
) {
  const query = usePropertyWindowExtensionQuery(propertyId, isLive)
  const extension = computed(() => (toValue(isLive) ? (query.data.value ?? null) : null))
  const isDialogOpen = ref(false)

  const signing = useAgreementSigning(() => extension.value?.listingRequestId ?? '', {
    returnPath: () =>
      toValue(options.returnPath) ?? routes.PROPERTY_DETAIL.replace(':id', toValue(propertyId)),
    onDeclined: () => {
      isDialogOpen.value = false
      void query.refetch()
    },
  })

  function ownTarget() {
    const proposal = extension.value
    if (!proposal?.ownSignatoryId) return null
    return { agreementId: proposal.agreementId, signatoryId: proposal.ownSignatoryId }
  }

  function acceptExtension() {
    const target = ownTarget()
    if (target) signing.startSigning(target)
  }

  function declineExtension(reason: string) {
    const target = ownTarget()
    if (!target) return
    signing.openDecline(target)
    signing.confirmDecline(reason)
  }

  return {
    extension,
    extensionNotice: computed(() => extensionNoticeOf(extension.value)),
    isActionNeeded: computed(() => isExtensionActionNeeded(extension.value)),
    isDialogOpen,
    isAccepting: computed(() => signing.pendingSignatoryId.value !== null),
    isDeclining: signing.isDeclining,
    declineError: signing.declineError,
    acceptExtension,
    declineExtension,
  }
}
